import type { ContextType } from 'react';
import React, { Component } from 'react';
import type { RouteComponentProps } from 'react-router-dom';
import { Link } from 'react-router-dom';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';
import debounce from 'lodash.debounce';
import type { Location } from 'history';

import { cn } from 'helpers/classnames';
import RecosCantFindYourSize from 'components/productdetail/RecosCantFindYourSize';
import { trackPageEvent } from 'utils/contentSquare/uxaHelper';
import EventName from 'utils/contentSquare/eventNames';
import type { SizeChartModalContent, SizeChartModalProps } from 'components/common/SizeChartModal';
import SizeChartModal from 'components/common/SizeChartModal';
import LazyHowItWasWorn from 'components/productdetail/LazyHowItWasWorn';
import { AFTER_PAY_JS } from 'constants/externalJavascriptFiles';
import { PRODUCT_PAGE } from 'constants/amethystPageTypes';
import { LazyAsk } from 'containers/LazyAsk';
import { evCantFindYourSizeClick, evProductLowestRecentPrice, pvProduct } from 'events/product';
import { evAddToCart } from 'events/cart';
import { evRecommendationClick } from 'events/recommendations';
import { OutOfStockModalWrapper, ProductNotifyModal } from 'components/productdetail/asyncProductPageModals';
import OutOfStockPopoverWrapper from 'components/productdetail/OutOfStockPopoverWrapper';
import { getHearts, heartProduct, toggleHeartingLoginModal, unHeartProduct } from 'actions/hearts';
import { trackEvent, trackLegacyEvent } from 'helpers/analytics';
import { firePixelEvent } from 'actions/pixelServer';
import { getHeartProps } from 'helpers/HeartUtils';
import { pageTypeChange } from 'actions/common';
import { addAdToQueue, updateAdData } from 'actions/ads';
import { createAddToCartMicrosoftUetEvent, pushMicrosoftUetEvent } from 'actions/microsoftUetTag';
import { onLookupRewardsTransparencyPointsForItem } from 'store/ducks/rewards/actions';
import { buildProductPageRecoKey, getRecoSlotKey, getRecosSlot, RECOS_CANT_FIND_YOUR_SIZE_KEY } from 'helpers/RecoUtils';
import { fetchAccountInfo } from 'actions/account/account';
import {
  fetchLowestPrices,
  fetchProductSearchSimilarity,
  fetchSizingPrediction,
  firePdpPixelServer,
  getPdpStoriesSymphonyComponents,
  hideSelectSizeTooltip,
  highlightSelectSizeTooltip,
  loadProductDetailPage,
  productAgeGroupChanged,
  productGenderChanged,
  productSingleShoeSideChanged,
  productSizeChanged,
  productSizeRangeChanged,
  productSizeUnitChanged,
  productSwatchChange,
  selectedColorChanged,
  setProductDocMeta,
  showSelectSizeTooltip,
  toggleOosButton,
  unhighlightSelectSizeTooltip,
  validateDimensions
} from 'actions/productDetail';
import { fetchProductReviews, fetchProductReviewsWithMedia, hideReviewGalleryModal, showReviewGalleryModal } from 'actions/reviews';
import { toggleProductNotifyModal } from 'actions/productdetail/sharing';
import { setLastSelectedSize } from 'actions/lastSelectedSizes';
import { submitNotifyBrandEmail } from 'actions/brand';
import { changeQuantity, showCartModal } from 'actions/cart';
import { fetchProductPageRecos } from 'actions/recos';
import { translateCartError } from 'apis/mafia';
import { PageLoader } from 'components/Loader';
import SiteAwareMetadata from 'components/SiteAwareMetadata';
import SocialCollectionsWidget from 'components/account/Collections/SocialCollectionsWidget';
import ProductBreadcrumbs from 'components/productdetail/ProductBreadcrumbs';
import ProductName from 'components/productdetail/ProductName';
import StylePicker from 'components/productdetail/stylepicker/StylePicker';
import ProductGallery from 'components/productdetail/productGallery/ProductGallery';
import HappyFeatureFeedback from 'components/HappyFeatureFeedback';
import RecosDetail1 from 'components/productdetail/RecosDetail1';
import RecosDetail2 from 'components/productdetail/RecosDetail2';
import RecosDetail3 from 'components/productdetail/RecosDetail3';
import ReviewPreview from 'components/productdetail/ReviewPreview';
import { indefiniteArticleSelector, openPopup, stripSpecialCharsDashReplace } from 'helpers/index';
import { usdToNumber } from 'helpers/NumberFormats';
import ProductUtils, { getProductImagesFormatted } from 'helpers/ProductUtils';
import { isDesktop, offset } from 'helpers/ClientUtils';
import { loadScript } from 'helpers/ScriptUtils';
import { buildSeoBrandString, buildSeoProductString, buildSeoProductUrl } from 'helpers/SeoUrlBuilder';
import Price from 'components/productdetail/Price';
import ShippingAndReturnsBanner from 'components/productdetail/ShippingAndReturnsBanner';
import ReviewPhotoGallery from 'components/productdetail/ReviewPhotoGallery';
import ReviewGalleryWrapper from 'containers/ReviewGalleryWrapper';
import { POPUP_CLASS_RE } from 'common/regex';
import { shouldRenderMostHelpfulReviews, shouldRenderReviewGallery } from 'helpers/ReviewUtils';
import { apstagRemoveToken, apstagUpdateToken, PDP_NARROW_MID, PDP_NARROW_TOP, PDP_WIDE_MID, PDP_WIDE_TOP } from 'helpers/apsAdvertisement';
import { onEvent } from 'helpers/EventHelpers';
import { titaniteView, track } from 'apis/amethyst';
import { stockSelectionCompleted } from 'store/ducks/productDetail/actions';
import GamSlot from 'components/common/GamSlot';
import JanusPixel from 'components/common/JanusPixel';
import Image from 'components/common/Image';
import { StructuredVideoObject } from 'components/common/StructuredDataTypes';
import LandingSlot from 'containers/LandingSlot';
import { ProductContextProvider } from 'components/productdetail/ProductContext';
import BuyBoxReviewBlurb from 'components/productdetail/BuyBoxReviewBlurb';
import type { FormattedProductBundle, ProductDetailState, StyleThumbnail } from 'reducers/detail/productDetail';
import type { ProductBrand, ProductSizing, ProductStockData, ProductStyle, SizingValue } from 'types/cloudCatalog';
import type { FeaturedImage, ProductDimensionValidation, SelectedSizing } from 'types/product';
import type { RecosState } from 'reducers/recos';
import type { AppState } from 'types/app';
import { MartyContext } from 'utils/context';
import Gallery from 'components/productdetail/productGallery/Gallery';
import ProductGalleryOverlay from 'components/productdetail/productGallery/ProductGalleryOverlay';
import { getProductRelations } from 'actions/products';
import Badge from 'components/common/Badge';
import { PromoBadge } from 'components/common/PromoBadge/PromoBadge';
import PdpSponsoredAds from 'components/productdetail/PdpSponsoredAds';
import { fetchCustomerAuthDetails } from 'actions/authentication';
import { selectSymphonyStory } from 'selectors/product';
import { UnleashWiringTest } from 'components/common/UnleashWiringTest';
import ProductSkuNumber from 'components/productdetail/ProductSkuNumber';
import { PAGE_TYPES } from 'constants/appConstants';
import { SIZE_CHART_ACCORDION_HEADING } from 'constants/product';
import { ProductAccordions } from 'components/productdetail/accordions/ProductAccordions';
import ShareSocialLinks from 'components/common/ShareSocialLinks';
import { FEATURE_PDP_AMAZON_REVERSE_SHARING, FEATURE_PDP_CLOUDCAT_DIRECT, FEATURE_PDP_KRATOS, FEATURE_PDP_OOS_UX } from 'constants/features';
import { useIsEnrolledInVariant } from 'hooks/unleash/useIsEnrolledInVariant';
import AmazonReverseSharingCarousel from 'components/productdetail/AmazonReverseSharingCarousel/AmazonReverseSharingCarousel';
import { fetchAmazonReverseSharing } from 'actions/amazonReverseSharing';
import { ZPromo } from 'components/productdetail/ZPromo/ZPromo';
import type { FormattedJanusReco } from 'types/janus';
import { fireAmplitudeEvent } from 'utils/amplitude';
import type { AmplitudeRecoClickEvent } from 'types/amplitude';

import css from 'styles/containers/productDetail.scss';

/**
 * This definitely doesn't feel great, but `SiteAwareMetadata` does some funny business with higher-order-functions,
 * so until that file is typed, we need to cast it.
 */
const SiteMetadata = SiteAwareMetadata as any;

const recosDetail1Ref = React.createRef<HTMLHeadingElement>();

interface State {
  isLinkCopied: boolean;
  isSpotlightActive: boolean;
  mouseCoordinates: any;
  movementRatioX: number;
  movementRatioY: number;
  sizeChartModalProps: SizeChartModalProps;
  spotlightHiResImageHeight: number;
  spotlightHiResImageSrc: string | null;
  spotlightHiResImageWidth: number;
  spotlightLowResImageSrc: string | null;
  spotlightWrapperHeight: number;
  isVideo: boolean;
}

interface Params {
  productId: string;
  colorId?: string;
  seoName?: string;
}

type Props = PropsFromRedux & RouteComponentProps<Params, {}> & typeof defaultProps;

const defaultProps = {
  enableSlideUpHeader: true,
  trackEvent,
  trackLegacyEvent,
  firePixelEvent,
  isPdpOosUx: false,
  isAmazonReverseSharingEnabled: false
};

export type OpenSizeChartModal = typeof ProductDetail.prototype.openSizeChartModal;

// requires productId from path param with optional colorId from path.
// Trigger assignments here for SSR components
export class ProductDetail extends Component<Props, State> {
  static fetchDataOnServer(store: any, _location: Location, { productId, colorId, seoName }: Params) {
    const { dispatch } = store;
    return dispatch(
      loadProductDetailPage(productId, {
        colorId,
        seoName,
        firePixel: true,
        includeOosSizing: false
      })
    );
  }

  static defaultProps = defaultProps;

  constructor(props: Props) {
    super(props);
    this.onStockChange = this.onStockChange.bind(this);
    this.onRecoClicked = this.onRecoClicked.bind(this);
    this.onSwatchStyleChosen = this.onSwatchStyleChosen.bind(this);
    this.onHideSelectSizeTooltip = this.onHideSelectSizeTooltip.bind(this);
    this.unhighlightAndHideSelectSizeTooltip = this.unhighlightAndHideSelectSizeTooltip.bind(this);
    this.getProductRelationsDebounced = debounce(this.getProductRelationsDebounced.bind(this), 350);
    this.checkForHeartsAndProductRelations = this.checkForHeartsAndProductRelations.bind(this);
    this.getRecosToDisplay = this.getRecosToDisplay.bind(this);
    this.isVideoSelected = this.isVideoSelected.bind(this);
  }

  state: State = {
    isSpotlightActive: false,
    isLinkCopied: false,
    sizeChartModalProps: { isOpen: false },
    spotlightLowResImageSrc: '',
    spotlightHiResImageSrc: '',
    spotlightHiResImageWidth: 0,
    spotlightHiResImageHeight: 0,
    spotlightWrapperHeight: 0,
    mouseCoordinates: {},
    movementRatioX: 0,
    movementRatioY: 0,
    isVideo: false
  };

  componentDidMount() {
    titaniteView();
    const {
      addAdToQueue,
      getHearts,
      pageTypeChange,
      product,
      fetchAccountInfo,
      fetchCustomerAuthDetails,
      adCustomerId,
      adEmailHash,
      holmes,
      updateAdData,
      location
    } = this.props;

    const { detail } = product;

    const {
      marketplace: {
        features: { showReviews },
        pdp: { hasSymphonyStories, showSizingPrediction, hasLowestRecentPrice },
        checkout: { allowAfterPay },
        hasApstagAdsToken,
        name
      }
    } = this.context;

    // todo
    /* This "apstagRemoveToken" function is added to remove the cookies that was generated to personalise the apstag ads on 6pm site. Currently the functionality is
        turned off but the cookies are not yet removed. They will automatically get removed after their lifetime, i.e. 14 days from turn off - we
        can remove this function after 9th Feb 2023. We are adding this function to make sure that users are not getting personalised content when
        personalisation is turned off from our side. */
    if (name === '6pm.com') {
      apstagRemoveToken();
    }

    if (hasApstagAdsToken) {
      const customerId = holmes ? holmes.customerId : '';
      apstagUpdateToken(customerId, adCustomerId, adEmailHash, fetchAccountInfo, updateAdData, fetchCustomerAuthDetails);
    }

    if (allowAfterPay) {
      loadScript({
        src: AFTER_PAY_JS,
        id: 'afterpay-bundle',
        onLoadCallback: null,
        onLoadErrorCallback: null
      });
    }

    pageTypeChange(PAGE_TYPES.PRODUCT_DETAIL_PAGE);
    firePixelEvent('product');

    const {
      product: { reviewData, sizingPredictionId },
      params: { productId, colorId, seoName },
      fetchProductReviews,
      fetchProductReviewsWithMedia,
      fetchSizingPrediction,
      isShowingThirdPartyAds,
      getPdpStoriesSymphonyComponents,
      productSwatchChange,
      setProductDocMeta,
      toggleProductNotifyModal,
      trackEvent,
      fetchLowestPrices,
      selectedColorChanged,
      isProductTypeShoesOrClothing,
      firePdpPixelServer
    } = this.props;
    const { styles, defaultProductType, productId: detailProductId } = detail || {};

    if (isShowingThirdPartyAds) {
      const slots = [{ name: PDP_NARROW_MID }, { name: PDP_NARROW_TOP }, { name: PDP_WIDE_MID }, { name: PDP_WIDE_TOP }];

      addAdToQueue(slots);
    }

    // Setting event watcher for popup link clicks set by content
    onEvent(
      document.body,
      'click',
      (e: React.MouseEvent) => {
        const { target } = e;
        const element = target as HTMLElement;
        if (element && element.tagName === 'A' && POPUP_CLASS_RE.test(element.className)) {
          openPopup(e as React.MouseEvent<HTMLAnchorElement>);
        }
      },
      undefined,
      this
    );

    onEvent(document.body, 'click', this.onBuyBoxPageContentClick, undefined, this);

    // Updates airplane cache to avoid stale sizing data when changing the product
    // color and navigating back an forth to the same previous pdp page.
    const airplaneConstraintColorId = detail?.sizing.airplaneCache?.constraints.colorId;
    if (airplaneConstraintColorId !== colorId) {
      selectedColorChanged(colorId);
    }

    // if we client routed we need to load everything
    if (!detail || detailProductId !== productId || (styles && styles.length === 0)) {
      this.fetchData(productId);
    } else if (styles) {
      setProductDocMeta(detail, colorId, isProductTypeShoesOrClothing);
      this.getRewardsTransparencyData();
      // product data is loaded, but non-critical pieces may not
      if (window.pageYOffset === 0 && !isDesktop()) {
        this.slideUpHeader();
      }

      if (hasSymphonyStories && productId) {
        getPdpStoriesSymphonyComponents(productId);
      }

      if (showReviews && (!reviewData || reviewData.productId !== productId || reviewData.page !== 1 || reviewData.offset !== 0)) {
        fetchProductReviews(productId, 1, 0, false);
        fetchProductReviewsWithMedia(productId);
      }

      if (hasLowestRecentPrice) {
        fetchLowestPrices(productId);
      }

      // If we direct link to an OOS product, we fall back to the first available style
      // In this instance (if no in stock style matches our colorId from the URL parameter)
      // Make sure we "select" that first style
      if (colorId && !styles.some(style => style.colorId === colorId)) {
        const { colorId: newColorId } = styles[0]!;
        productSwatchChange(newColorId);
        this.useSeoUrl(detail, newColorId);
      }

      if (detailProductId === productId && seoName !== buildSeoProductString(detail, colorId)) {
        this.useSeoUrl(detail, colorId);
      }

      if (showSizingPrediction && colorId && !sizingPredictionId && ProductUtils.isShoeType(defaultProductType)) {
        fetchSizingPrediction(detail, productId, colorId);
      }

      this.trackPageView(detail);
    }
    if (detailProductId) {
      this.checkForRecos(detailProductId, styles, { colorId });

      // Fetch Amazon Reverse Sharing linked styles
      const { fetchAmazonReverseSharing, isAmazonReverseSharingEnabled } = this.props;
      if (isAmazonReverseSharingEnabled && fetchAmazonReverseSharing) {
        fetchAmazonReverseSharing(detailProductId);
      }
    }
    trackEvent('TE_PV_PDP', productId);
    if (detail) {
      firePdpPixelServer(detail, colorId, holmes);
    }

    if (location.hash) {
      if (location.hash.indexOf('showModal=notifyoos') > -1) {
        toggleProductNotifyModal(true);
      }
    }
    // Get hearting list
    getHearts();

    this.trackLowestRecentPriceView();
  }

  componentDidUpdate(prevProps: Props) {
    const {
      params,
      trackEvent,
      product: { detail },
      showCartModal,
      params: { colorId }
    } = prevProps;

    const { productId } = detail || {};

    const {
      isCartModalShowing,
      product: { isSimilarStylesLoading, detail: nextDetail, lowestPrices },
      params: { productId: nextProductId, colorId: nextColorId, seoName: nextSeoName },
      fetchSizingPrediction,
      toggleOosButton,
      router
    } = this.props;

    // In some cases, lowestPrices takes longer to fetch, this can cause the related Amethyst event not to trigger.
    const sameLowestPrices =
      lowestPrices?.length &&
      prevProps.product.lowestPrices?.length === lowestPrices?.length &&
      prevProps.product.lowestPrices?.every((price, index) => price === lowestPrices[index]);

    if (!sameLowestPrices) this.trackLowestRecentPriceView();

    const {
      marketplace: {
        pdp: { showSizingPrediction }
      }
    } = this.context;

    const { productId: nextDetailProductId, styles: nextStyles, defaultProductType: nextProductType } = nextDetail || {};

    if (nextProductId !== params.productId) {
      titaniteView();
      this.fetchData(nextProductId);
      trackEvent('TE_PV_PDP', nextProductId);
    }

    const newProductLoaded = nextDetail && (productId !== nextDetailProductId || !detail);
    const newColorId = nextDetail && colorId !== nextColorId;
    // update pretty URL if the product data for a new product has loaded
    if (nextDetail && nextDetailProductId === nextProductId) {
      if (newProductLoaded && buildSeoProductString(nextDetail, nextColorId) !== nextSeoName && nextColorId) {
        this.useSeoUrl(nextDetail, nextColorId);
      }

      // if the color changed but the product didn't, fetch new recos.
      if (!newProductLoaded && colorId !== nextColorId) {
        isCartModalShowing && showCartModal(false);
        toggleOosButton(false);
        this.checkForRecos(nextProductId, nextStyles, { colorId: nextColorId });
        this.trackLowestRecentPriceView();
      }
    }

    if (newColorId || newProductLoaded) {
      this.getRewardsTransparencyData(); // if colorId differs from next colorId, make the call again
    }
    // for client routing, this block is for calls we need to make once the product itself has loaded.
    if (newProductLoaded) {
      if (nextDetail) {
        this.trackPageView(nextDetail);
        this.trackLowestRecentPriceView();
      }
      if (window.pageYOffset === 0 && !isDesktop()) {
        this.slideUpHeader();
      }

      if (showSizingPrediction && ProductUtils.isShoeType(nextProductType) && nextColorId) {
        fetchSizingPrediction(nextDetail, nextProductId, nextColorId);
      }

      this.checkForRecos(nextDetailProductId || nextProductId, nextStyles, {
        colorId: nextColorId
      });

      // Re-fetch Amazon Reverse Sharing linked styles for the new product
      const { fetchAmazonReverseSharing, isAmazonReverseSharingEnabled } = this.props;
      if (isAmazonReverseSharingEnabled && fetchAmazonReverseSharing) {
        fetchAmazonReverseSharing(nextDetailProductId || nextProductId);
      }
    }

    if (!isSimilarStylesLoading) {
      this.checkForHeartsAndProductRelations(prevProps, this.props);
    }

    // if we click on a color swatch or reco we don't want to scroll back to top ( REPLACE )
    // if we came from a reco click and press back button we do want to suppress scrolling ( POP )
    // if we come from simple productDetail, search then we want to scroll back to top ( PUSH )
    // only do this if products have changed
    if (router?.action === 'PUSH' && productId !== nextProductId) {
      window?.scrollTo(0, 0);
    }
  }

  componentWillUnmount() {
    const { isCartModalShowing, hideReviewGalleryModal, showCartModal } = this.props;
    const { isSpotlightActive } = this.state;
    isCartModalShowing && showCartModal(false);
    hideReviewGalleryModal();

    // we probably don't need to do this, since the component is being unmounted let's cleanup
    if (isSpotlightActive) {
      this.zoomOut();
    }
  }

  static contextType = MartyContext;
  // @ts-ignore https://github.zappos.amazon.dev/mweb/marty/issues/19273
  context!: ContextType<typeof MartyContext>;

  // Types for function-bound refs on `this`
  buyBox: undefined | null | HTMLDivElement;
  theater: undefined | null | HTMLDivElement;
  spotlightWrapper: undefined | null | HTMLButtonElement;
  spotlight: undefined | null | HTMLDivElement;

  getRewardsTransparencyData = () => {
    const {
      marketplace: { hasRewardsTransparency }
    } = this.context;
    const { onLookupRewardsTransparencyPointsForItem, isGiftCard } = this.props;
    if (!isGiftCard && hasRewardsTransparency) {
      onLookupRewardsTransparencyPointsForItem();
    }
  };

  getRecosToDisplay(slot: 'slot0' | 'slot1' | 'slot2' | 'slot3' | 'cfys', props: Props) {
    const {
      similarProductRecos: recoStoreData,
      product: { detail },
      params
    } = props;

    const { colorId } = params || {};

    const { productId, styles } = detail || {};

    if (!styles) {
      return;
    }

    const style = ProductUtils.getStyleByColor(styles, colorId);
    const { lastReceivedRecoKey, janus: janusRecos = {} } = recoStoreData;
    if (productId) {
      const recosForProduct = janusRecos[buildProductPageRecoKey(productId, style?.styleId)] || janusRecos;
      return getRecosSlot(recosForProduct, slot, lastReceivedRecoKey);
    }
  }

  checkForHeartsAndProductRelations(props: Props, nextProps: Props) {
    const detail1 = this.getRecosToDisplay('slot1', props);
    const nextDetail1 = this.getRecosToDisplay('slot1', nextProps);
    const nextDetail1Recos = nextDetail1?.recos;

    const detail2 = this.getRecosToDisplay('slot2', props);
    const nextDetail2 = this.getRecosToDisplay('slot2', nextProps);
    const nextDetail2Recos = nextDetail2?.recos;

    const detailCfys = this.getRecosToDisplay('cfys', props);
    const nextDetailCfys = this.getRecosToDisplay('cfys', nextProps);
    const nextDetailCysRecos = nextDetailCfys?.recos;

    if (detail1?.recos !== nextDetail1Recos || detail2?.recos !== nextDetail2Recos || detailCfys?.recos !== nextDetailCysRecos) {
      const allRecos = [...(nextDetail1Recos || []), ...(nextDetail2Recos || []), ...(nextDetailCysRecos || [])];

      // get product relations for displaying swatches for recos
      const styleIds = allRecos.reduce((acc, { styleId }) => {
        !acc.includes(styleId) && acc.push(styleId);
        return acc;
      }, []);
      if (styleIds.length) {
        this.getProductRelationsDebounced(styleIds);
      }
    }
  }

  fetchData(productId: string) {
    const {
      loadProductDetailPage,
      fetchProductReviews,
      fetchProductReviewsWithMedia,
      getPdpStoriesSymphonyComponents,
      fetchLowestPrices,
      params: { colorId, seoName }
    } = this.props;
    const {
      marketplace: {
        pdp: { hasSymphonyStories, hasLowestRecentPrice },
        features: { showReviews }
      }
    } = this.context;

    const { isSpotlightActive } = this.state;
    if (isSpotlightActive) {
      this.zoomOut();
    }

    loadProductDetailPage(productId, {
      colorId,
      seoName,
      firePixel: true,
      includeOosSizing: false
    });

    if (hasSymphonyStories) {
      getPdpStoriesSymphonyComponents(productId);
    }

    if (showReviews) {
      fetchProductReviews(productId, 1, 0, false);
      fetchProductReviewsWithMedia(productId);
    }

    if (hasLowestRecentPrice) {
      fetchLowestPrices(productId);
    }
  }

  getProductRelationsDebounced(styleIds: string[]) {
    this.props.getProductRelations(styleIds);
  }

  slideUpHeader() {
    if (!this.props.enableSlideUpHeader || typeof document === 'undefined') {
      return;
    }
  }

  checkForRecos(productId: string, styles: ProductStyle[] = [], { colorId }: { colorId: string | undefined }, isCFYSslot?: boolean) {
    const { fetchProductPageRecos } = this.props;
    const productStyleId = (ProductUtils.getStyleByColor(styles, colorId) || {}).styleId;

    if (!productStyleId) {
      return;
    }

    fetchProductPageRecos(productId, productStyleId, !!isCFYSslot);
  }

  useSeoUrl(product: FormattedProductBundle, colorId?: string) {
    this.context.router.replacePreserveAppRoot(this.buildSeoUrl(product, colorId));
  }

  buildSeoUrl(product: FormattedProductBundle, colorId?: string) {
    const { search, hash } = this.props.location;
    return `${buildSeoProductUrl(product, colorId)}${search ? search : ''}${hash || ''}`;
  }

  showAndHighlightSelectSizeTooltip() {
    const { showSelectSizeTooltip, highlightSelectSizeTooltip } = this.props;
    showSelectSizeTooltip();
    highlightSelectSizeTooltip();
  }

  handleValidationClick = () => {
    this.showAndHighlightSelectSizeTooltip();
    validateDimensions(true);
  };

  unhighlightAndHideSelectSizeTooltip() {
    const { hideSelectSizeTooltip, unhighlightSelectSizeTooltip } = this.props;
    unhighlightSelectSizeTooltip();
    hideSelectSizeTooltip();
  }

  setLastSelectedSizes() {
    const {
      setLastSelectedSize,
      product: { detail, selectedSizing }
    } = this.props;
    const {
      sizing: { hypercubeSizingData = {} }
    } = detail as FormattedProductBundle;

    const gender = ProductUtils.getGender(detail);
    Object.entries(selectedSizing).forEach(([dimensionId, sizeId]) => {
      if (sizeId) {
        const range = hypercubeSizingData[sizeId];
        if (gender && dimensionId && range) {
          setLastSelectedSize(gender, dimensionId, sizeId, range.min, range.max);
        }
      }
    });
  }

  onAddToCart = (e: React.MouseEvent<HTMLButtonElement> | React.FormEvent<HTMLFormElement>, isSticky?: boolean, isStickyBelowFold?: boolean) => {
    const { changeQuantity, product, pushMicrosoftUetEvent, trackLegacyEvent, toggleOosButton, showCartModal } = this.props;
    const { detail, selectedSizing, colorId } = product;
    const { styles, sizing } = detail as FormattedProductBundle;
    const style = ProductUtils.getStyleByColor(styles, colorId);
    const currentColorId = colorId || style.colorId;
    const stock = ProductUtils.getStockBySize(sizing.stockData, currentColorId, selectedSizing);
    const asin = ProductUtils.getSelectedStyleStockBySize(undefined, sizing, selectedSizing, style.stocks)?.asin || undefined;
    e.preventDefault();
    if (!stock) {
      toggleOosButton(true);
    } else {
      const { onHand } = stock as ProductStockData;
      let { stockId: { value: stockId } = { value: undefined } } = e.target as HTMLFormElement;

      if (!stockId) {
        ({
          dataset: { stockId } = {
            stockId: undefined
          }
        } = e.currentTarget as HTMLElement);
      }
      /* To differentiate sticky vs non-sticky add to cart, the sticky button fires this method via onClick (opposed to the native form submit)
               We need to read the stockID from the button directly, since the event doesn't have access to the hidden stockId input in AddToCart.jsx
            */
      if (stockId && ProductUtils.isSizeSelectionComplete(sizing, selectedSizing)) {
        trackLegacyEvent('CartAddItem', null, `stockId:${stockId}|styleId:${style.styleId}`);
        const amethystEventBundle = {
          ...style,
          addedFrom: PRODUCT_PAGE,
          isSticky,
          isStickyBelowFold
        };
        track(() => [evAddToCart, amethystEventBundle]);
        pushMicrosoftUetEvent(createAddToCartMicrosoftUetEvent(stockId, +usdToNumber(style.price), 'product'));
        this.setLastSelectedSizes();
        changeQuantity(
          {
            items: [
              {
                stockId,
                quantity: 1,
                quantityAddition: true,
                onHand,
                trackAsin: asin
              }
            ]
          },
          { firePixel: true }
        ).then(response => {
          const error = translateCartError(response);
          if (error) {
            alert(error);
          } else {
            showCartModal(true, stockId);
          }
        });
      } else {
        this.handleAddToCartIncomplete(style, sizing, selectedSizing, isSticky, isStickyBelowFold);
      }
    }
  };

  // re-usable handler for onAddToCart cases where sizing selection is incomplete
  handleAddToCartIncomplete = (
    style: ProductStyle,
    sizingData: ProductSizing,
    selectedSizing: SelectedSizing,
    isSticky?: boolean,
    isStickyBelowFold?: boolean
  ) => {
    this.showAndHighlightSelectSizeTooltip();
    this.props.validateDimensions(true);
    const firstInvalidDimensionName = ProductUtils.getMissingDimensionName(selectedSizing, sizingData);
    const missingDimension = firstInvalidDimensionName ? `${firstInvalidDimensionName}_dimension`.toUpperCase() : 'UNKNOWN_PRODUCT_DIMENSION';
    track(() => [
      evAddToCart,
      {
        ...style,
        incompleteAddToCart: true,
        missingDimension,
        addedFrom: PRODUCT_PAGE,
        isSticky,
        isStickyBelowFold
      }
    ]);
    // Do not scroll or show the alert dialog on desktop
    if (!isDesktop()) {
      if (this.buyBox) {
        this.buyBox.scrollIntoView(true);
      }
      // scroll into view is async, so we want the elements to be in view when they see this alert.
      setTimeout(() => {
        alert(`Please select ${indefiniteArticleSelector(firstInvalidDimensionName)} ${firstInvalidDimensionName}.`);
      }, 25);
    }
  };

  getStyleId = () => {
    const {
      product: { detail, colorId }
    } = this.props;
    if (detail) {
      const { styleId } = ProductUtils.getStyleByColor(detail?.styles, colorId);
      return styleId;
    } else {
      return;
    }
  };

  onStockChange(styleId: string, selectedSizing: SelectedSizing, { label }: { label: string | null; name?: string; selectedOption?: SizingValue }) {
    const {
      product: { detail },
      productSizeChanged,
      productSwatchChange,
      validateDimensions
    } = this.props;

    if (detail) {
      const { colorId } = ProductUtils.getStyleMap(detail.styles)[styleId]!;
      const { sizing } = detail;
      const style = ProductUtils.getStyleByColor(detail.styles, colorId);
      const stock = ProductUtils.getStockBySize(sizing.stockData, style.colorId, selectedSizing);

      if (!stock) {
        trackPageEvent(EventName.PDP_OUT_OF_STOCK);
      }

      if (label === 'color') {
        productSwatchChange(colorId);
        this.context.router.replacePreserveAppRoot(this.buildSeoUrl(detail, colorId));
      } else {
        productSizeChanged(selectedSizing);
        validateDimensions();
        if (ProductUtils.isSizeSelectionComplete(sizing, selectedSizing)) {
          this.unhighlightAndHideSelectSizeTooltip();
        }
      }
    }
  }

  onRecoClicked({
    index,
    amethystRecoType,
    recoSource,
    recommendedProduct,
    clickThrough,
    sourcePage,
    reco
  }: {
    index: number;
    amethystRecoType: string;
    recoSource?: string;
    reco: FormattedJanusReco;
    recommendedProduct?: {} | null | undefined;
    clickThrough: string;
    sourcePage?: string;
  }) {
    const gender = ProductUtils.getProductGender(reco?.txAttrFacet_Gender) ?? '';
    const isOnSale = ProductUtils.isStyleOnSale({ originalPrice: reco.originalPrice, price: reco.price });

    const productModule = {
      brand: reco.brandName,
      category: reco.productType ?? '',
      gender,
      list_price: ProductUtils.priceToFloat(reco.originalPrice),
      name: reco.productName,
      on_sale: isOnSale,
      price: reco.price,
      sku: reco.productId,
      styleId: reco.styleId,
      subCategory: '',
      subcategory2: '',
      position: index + 1
    };

    fireAmplitudeEvent<AmplitudeRecoClickEvent>({
      event: 'click',
      pageLang: 'en',
      pageType: 'pdp',
      component: {
        name: 'Recommendation',
        type: 'product',
        context: amethystRecoType
      },
      user: {
        directedID: this.props.directedId,
        login_status: this.props.isRecognizedCustomer ? 'AUTHED' : 'UNAUTHED',
        vip_status: this.props.isEnrolledInVip ? 'enrolled' : 'not_enrolled'
      },
      screen: {
        name: 'product',
        type: 'pdp'
      },
      sessionId: this.props.sessionId,
      product: productModule
    });

    track(() => [
      evRecommendationClick,
      {
        index,
        recommendationType: 'PRODUCT_RECOMMENDATION',
        recommendedProduct,
        recommendationSource: recoSource || 'EP13N',
        widgetType: amethystRecoType,
        clickThrough,
        sourcePage
      }
    ]);
  }

  openSizeChartModal = (content: SizeChartModalContent) => {
    this.setState({
      sizeChartModalProps: {
        content,
        isOpen: true,
        onRequestClose: this.closeSizeChartModal
      }
    });
  };

  closeSizeChartModal = () => {
    this.setState({ sizeChartModalProps: { isOpen: false } });
  };

  onOpenProductNotifyMe = (event: React.MouseEvent<HTMLButtonElement>) => {
    const {
      similarProductRecos: { janus },
      product: { detail },
      params: { productId, colorId },
      toggleProductNotifyModal,
      trackLegacyEvent
    } = this.props;

    event.preventDefault();
    trackLegacyEvent('Product-Page', 'OOS', 'Notify-Me');
    toggleProductNotifyModal(true);

    // Makes sure to only retrieve the recos once per product/style/color
    const productStyleId = (ProductUtils.getStyleByColor(detail?.styles || [], colorId) || {}).styleId;
    const recoKey = buildProductPageRecoKey(productId, productStyleId);
    const hasCFYSRecos = janus?.[recoKey]?.hasOwnProperty(getRecoSlotKey(RECOS_CANT_FIND_YOUR_SIZE_KEY));
    if (!hasCFYSRecos) {
      this.checkForRecos(productId, detail?.styles, { colorId }, true);
    }
  };

  onCloseProductNotifyMe = () => {
    this.props.toggleProductNotifyModal(false);
  };

  onOutofStockPopoverShown = () => {
    const {
      params: { productId, colorId },
      product: { detail },
      fetchProductSearchSimilarity
    } = this.props;
    if (detail) {
      const { styles } = detail;
      const style = ProductUtils.getStyleByColor(styles, colorId);
      if (style) {
        fetchProductSearchSimilarity(productId, style.styleId);
      }
    }
  };

  showNoSizeSelected = (sizing: ProductSizing, selectedSizing: SelectedSizing) => {
    const { validateDimensions } = this.props;

    this.showAndHighlightSelectSizeTooltip();
    validateDimensions(true);
    const firstInvalidDimensionName = ProductUtils.getMissingDimensionName(selectedSizing, sizing);

    // Do not show the alert dialog on desktop
    if (!isDesktop()) {
      alert(`Please select a ${firstInvalidDimensionName}.`);
    }
  };

  handleThumbnailClick = () => {
    const { isSpotlightActive } = this.state;
    if (isSpotlightActive) {
      this.zoomOut();
    }
    trackLegacyEvent('Product-Page', 'PrImage', 'Thumbnail-Swap-Click');
  };

  handleProductImageClick = (
    lowResImageSrc: string,
    hiResImageSrc: string,
    lowResImageWidth: number,
    lowResImageHeight: number,
    mouseCoordinates: { pageX: number; pageY: number }
  ) => {
    this.setState(
      {
        isSpotlightActive: true,
        spotlightLowResImageSrc: lowResImageSrc,
        spotlightHiResImageSrc: hiResImageSrc,
        spotlightHiResImageWidth: lowResImageWidth * 4,
        spotlightHiResImageHeight: lowResImageHeight * 4,
        mouseCoordinates
      },
      () => {
        this.setHeightsAndOffsets();
      }
    );
  };

  setHeightsAndOffsets = () => {
    const {
      marketplace: {
        pdp: { spotlightProductImagesId }
      }
    } = this.context;
    const productImagesEl = document.querySelector(spotlightProductImagesId);
    const productImagesOffset = offset(productImagesEl);
    /**
     * Test case for spotlight zoom calculation (this fixes an issue with zoom feature not calculation properly)
     */
    const spotlightWrapperHeight = productImagesOffset?.height || 500;
    // Calculation fix (should always relies on the height of the container - which varies in position and size)
    // default min-height for the productImage container

    this.setState({ spotlightWrapperHeight }, () => {
      this.calcMovementRatios();
    });
  };

  handleSpotlightWrapperMouseMove = (event: React.MouseEvent) => {
    this.moveSpotlight(event);
  };

  zoomOut = () => {
    this.setState({
      spotlightLowResImageSrc: null,
      spotlightHiResImageSrc: null,
      spotlightHiResImageWidth: 0,
      spotlightHiResImageHeight: 0,
      spotlightWrapperHeight: 0,
      movementRatioX: 0,
      movementRatioY: 0,
      mouseCoordinates: {},
      isSpotlightActive: false
    });
  };

  isVideoSelected = (productAssetType: boolean) => this.setState({ isVideo: productAssetType });

  calcMovementRatios = () => {
    if (this.spotlightWrapper) {
      const { spotlightWrapperHeight } = this.state;
      const spotlightWrapperWidth = Math.abs(this.spotlightWrapper.offsetWidth);
      const { spotlightHiResImageWidth, spotlightHiResImageHeight, mouseCoordinates } = this.state;

      this.setState(
        {
          movementRatioX: (spotlightHiResImageWidth - spotlightWrapperWidth) / spotlightWrapperWidth,
          movementRatioY: (spotlightHiResImageHeight - spotlightWrapperHeight) / spotlightWrapperHeight
        },
        () => {
          this.moveSpotlight(mouseCoordinates);
        }
      );
    }
  };

  moveSpotlight = (event: React.MouseEvent) => {
    if (this.spotlight && this.spotlightWrapper) {
      const { movementRatioX, movementRatioY } = this.state;
      const { left: offsetLeft = 0, top: offsetTop = 0 } = offset(this.spotlightWrapper);
      const relativeX = event.pageX - offsetLeft;
      const relativeY = event.pageY - offsetTop;

      const spotlightLeft = relativeX * movementRatioX;
      const spotlightTop = relativeY * movementRatioY;

      this.spotlight.style.left = `${-spotlightLeft}px`;
      this.spotlight.style.top = `${-spotlightTop}px`;
    }
  };

  makeSpotlight = () => {
    const {
      isSpotlightActive,
      spotlightWrapperHeight,
      spotlightLowResImageSrc,
      spotlightHiResImageSrc,
      spotlightHiResImageWidth,
      spotlightHiResImageHeight
    } = this.state;
    const { testId } = this.context;

    const style: { height?: number; display?: string } = {};
    if (spotlightWrapperHeight) {
      style.height = spotlightWrapperHeight;
    } else {
      style.display = 'none';
    }

    return (
      <>
        <button
          type="button"
          ref={el => (this.spotlightWrapper = el)}
          style={style}
          className={cn(css.spotlightWrapper, {
            [css.active]: isSpotlightActive
          })}
          onMouseMove={this.handleSpotlightWrapperMouseMove}
          tabIndex={isSpotlightActive ? 0 : -1}
          data-test-id={testId('zoomedImageFrame')}
        >
          <div ref={el => (this.spotlight = el)} className={css.spotlight} data-test-id={testId('spotlightImageContainer')}>
            {spotlightLowResImageSrc && (
              <img
                src={spotlightLowResImageSrc}
                className={css.spotlightLowResImage}
                width={spotlightHiResImageWidth}
                height={spotlightHiResImageHeight}
                alt="presentation"
              />
            )}
            {spotlightHiResImageSrc && (
              <img
                src={spotlightHiResImageSrc}
                className={css.spotlightHiResImage}
                width={spotlightHiResImageWidth}
                height={spotlightHiResImageHeight}
                alt="presentation"
              />
            )}
          </div>
        </button>
        <button
          type="button"
          data-test-id={testId('spotlightClose')}
          className={css.spotClose}
          tabIndex={isSpotlightActive ? 0 : -1}
          aria-label="Zoom out of product image"
        />
      </>
    );
  };

  onSwatchStyleChosen(event: React.MouseEvent) {
    event.preventDefault();
    const { currentTarget } = event;
    const { dataset: { styleId } = { styleId: undefined } } = currentTarget as HTMLElement;
    const { isSpotlightActive } = this.state;
    const {
      product: { selectedSizing }
    } = this.props;
    styleId && this.onStockChange(styleId, selectedSizing, { label: 'color' });
    if (isSpotlightActive) {
      this.zoomOut();
    }
  }

  onHideSelectSizeTooltip() {
    const {
      product: { isSelectSizeTooltipHighlighted },
      hideSelectSizeTooltip
    } = this.props;
    if (!isSelectSizeTooltipHighlighted) {
      hideSelectSizeTooltip();
    }
  }

  makeProductNotifyMe = () => {
    const { testId } = this.context;

    const onClickHander = (event: React.MouseEvent<HTMLButtonElement>) => {
      track(() => [evCantFindYourSizeClick, {}]);
      this.onOpenProductNotifyMe(event);
    };

    return (
      <div>
        <button type="button" className={css.notifyMeButton} onClick={onClickHander} data-test-id={testId('notifyMe')}>
          Can't Find Your Size? Get Notified.
        </button>
      </div>
    );
  };

  makeHeartsData = () => {
    const {
      marketplace: { hasHearting }
    } = this.context;
    const { isRecognizedCustomer, heartProduct, toggleHeartingLoginModal, trackEvent, unHeartProduct } = this.props;

    const heartProps = {
      hasHearting,
      isCustomer: isRecognizedCustomer,
      heartProduct,
      toggleHeartingLoginModal,
      trackEvent,
      unHeartProduct
    };

    const heartsData = getHeartProps(heartProps, {
      heartEventName: 'TE_PDP_HEART',
      unHeartEventName: 'TE_PDP_UNHEART'
    });

    return heartsData;
  };

  makeRecosFindYourSize = () => {
    const styles = this.props.product.detail?.styles || [];
    const style = ProductUtils.getStyleByColor(styles, this.props.params.colorId);
    const styleId = style && style.styleId;
    const heartsData = this.makeHeartsData();
    return (
      <RecosCantFindYourSize
        styleId={styleId}
        params={this.props.params}
        onRecoClicked={reco => {
          this.onRecoClicked({ ...reco, sourcePage: 'CANT_FIND_YOUR_SIZE_MODAL_PAGE' });
          this.onCloseProductNotifyMe();
        }}
        similarProductRecos={this.props.similarProductRecos}
        heartsData={heartsData}
        trackInViewSourcePage="CANT_FIND_YOUR_SIZE_MODAL_PAGE"
      />
    );
  };

  makeStylePicker({
    product,
    style,
    styleThumbnails,
    selectedSizing,
    dimensionValidation,
    sizingPredictionId,
    isOnDemandEligible
  }: {
    product: FormattedProductBundle;
    style: ProductStyle;
    styleThumbnails: StyleThumbnail[];
    selectedSizing: SelectedSizing;
    dimensionValidation: ProductDimensionValidation;
    sizingPredictionId: string | null;
    isOnDemandEligible: boolean | null | undefined;
  }) {
    const {
      location,
      symphonyStory: { slotData },
      product: { detail, isSelectSizeTooltipVisible, isSelectSizeTooltipHighlighted },
      isGiftCard,
      showSelectSizeTooltip,
      productSizeChanged,
      productAgeGroupChanged,
      productGenderChanged,
      productSingleShoeSideChanged,
      productSizeRangeChanged,
      productSizeUnitChanged,
      validateDimensions
    } = this.props;

    const productIsShoe = ProductUtils.isShoeType(detail?.defaultProductType);
    const isUnsexKidProduct = ProductUtils.getGender(detail) === 'kids';
    const removeGenderFilter = !!productIsShoe && isUnsexKidProduct;

    const {
      marketplace: {
        features: { showOOSNotifyMe, allowTwoDayShippingPrimePerk },
        pdp: { addToCartAction, hasRecommendedSizing, showRewardsCopy, showSizeChartLink, showSizeGender }
      }
    } = this.context;
    if (style && detail) {
      const { defaultImageUrl, brandName, productName, styles } = detail;

      return (
        <div id="buyBox" className={cn(css.stylePicker, css.noBorder)} ref={c => (this.buyBox = c)}>
          <div className={css.whiteBg}>
            <StylePicker
              makeProductNotifyMe={this.makeProductNotifyMe}
              styleList={styles}
              product={this.props.product}
              productId={product.productId}
              productType={product.defaultProductType}
              productImage={defaultImageUrl}
              productTitle={`${brandName} ${productName}`}
              sizing={product.sizing}
              genders={product.genders}
              selectedSizing={selectedSizing}
              selectedStyle={style}
              thumbnails={styleThumbnails}
              dimensionValidation={dimensionValidation.dimensions}
              onStockChange={this.onStockChange}
              onStyleChange={this.onSwatchStyleChosen}
              onAddToCart={this.onAddToCart}
              addToCartAction={addToCartAction}
              allowTwoDayShippingPrimePerk={allowTwoDayShippingPrimePerk}
              isGiftCard={isGiftCard}
              isSelectSizeTooltipVisible={isSelectSizeTooltipVisible}
              isSelectSizeTooltipHighlighted={isSelectSizeTooltipHighlighted}
              onShowSelectSizeTooltip={showSelectSizeTooltip}
              onHideSelectSizeTooltip={this.onHideSelectSizeTooltip}
              onUnhighlightSelectSizeTooltip={this.unhighlightAndHideSelectSizeTooltip}
              onValidationClick={this.handleValidationClick}
              showOosNotifyMe={showOOSNotifyMe}
              showSizeGender={showSizeGender}
              showSizeChartLink={showSizeChartLink}
              sizingPredictionId={sizingPredictionId}
              isOnDemandEligible={isOnDemandEligible}
              hasRecommendedSizing={hasRecommendedSizing}
              location={location}
              sizeSymphonyContent={slotData?.['buybox-size-1']}
              addToCartSymphonyContent={slotData?.['buybox-cart-1']}
              pageType={PRODUCT_PAGE}
              useDropdowns={false}
              useStickyAddToCart={true}
              productSizeChanged={productSizeChanged}
              productAgeGroupChanged={productAgeGroupChanged}
              productGenderChanged={productGenderChanged}
              productSingleShoeSideChanged={productSingleShoeSideChanged}
              productSizeRangeChanged={productSizeRangeChanged}
              productSizeUnitChanged={productSizeUnitChanged}
              validateDimensions={validateDimensions}
              removeGenderFilter={removeGenderFilter}
            />
            {showRewardsCopy && (
              <div className={css.rewardsCopy}>
                Join Zappos Rewards & get Free 2-Business Day shipping.
                <Link to="/zappos-rewards/" data-track-action="Product-Page" data-track-label="PrForm" data-track-value="Rewards">
                  Enroll now
                </Link>
              </div>
            )}
          </div>
        </div>
      );
    } else {
      return;
    }
  }

  onBack = (e: React.MouseEvent) => {
    e.preventDefault();
    this.context.router.goBack();
  };

  makeOutOfStock = () => {
    const {
      product: { detail, similarStyles, selectedSizing },
      brandPage,
      params,
      submitNotifyBrandEmail,
      productNotify,
      isPdpOosUx
    } = this.props;
    const {
      marketplace: {
        pdp: { showOutOfStockPopover },
        features: { showOOSNotifyMe, showUnifiedOOS }
      }
    } = this.context;
    const {
      notifyEmail: { submitted }
    } = brandPage;
    if (detail) {
      const { productId, brandId, brandName, styles, sizing } = detail;
      const productStyles = ProductUtils.getStyleMap(styles);
      const style = ProductUtils.getStyleByColor(styles, params.colorId);
      const stock = ProductUtils.getStockBySize(sizing.stockData, style.colorId, selectedSizing);

      // Different marketplaces show different popups when stock is not available
      if (showOutOfStockPopover) {
        return (
          <OutOfStockPopoverWrapper
            brandId={brandId}
            brandName={brandName}
            recos={similarStyles}
            stock={stock}
            isSubmitted={submitted}
            onBrandNotifySubmit={submitNotifyBrandEmail}
            onShow={this.onOutofStockPopoverShown}
          />
        );
      } else if (!isPdpOosUx && showOOSNotifyMe) {
        return (
          <OutOfStockModalWrapper
            detail={detail}
            productId={productId}
            style={style}
            sizing={sizing}
            selectedSizing={selectedSizing}
            productStyles={productStyles}
            onOpenProductNotifyMe={this.onOpenProductNotifyMe}
            isProductNotifyOpen={productNotify.modalShown}
            onCloseProductNotifyMe={this.onCloseProductNotifyMe}
            onStyleChange={this.onSwatchStyleChosen}
            showUnifiedOOS={showUnifiedOOS}
            renderRecos={this.makeRecosFindYourSize}
          />
        );
      } else {
        return;
      }
    } else {
      return null;
    }
  };

  makeBrandLogo({ brand }: { brand: ProductBrand }, secureImageBaseUrl: string) {
    const { id, name, headerImageUrl } = brand;
    const { testId } = this.context;
    if (headerImageUrl) {
      const imgProps = {
        src: `${secureImageBaseUrl}${headerImageUrl}`,
        alt: name
      };
      return (
        <Link
          className={css.brandNameLink}
          to={buildSeoBrandString(name, id)}
          title={name}
          data-track-action="Product-Page"
          data-track-label="Tabs"
          data-track-value="Brand-Logo"
          data-test-id={testId('brandLogo')}
        >
          <Image {...imgProps} />
        </Link>
      );
    } else {
      return (
        <p className={css.brandName}>
          <Link to={buildSeoBrandString(name, id)}>{name}</Link>
        </p>
      );
    }
  }

  handleReviewMediaClick = (reviewId: string, mediaIndex: number) => {
    this.props.showReviewGalleryModal(reviewId, mediaIndex);
  };

  makeAsk() {
    const {
      marketplace: {
        features: { showAsk }
      },
      testId
    } = this.context;
    const { params, product } = this.props;
    if (showAsk) {
      return (
        <div className={cn(css.askContainer, css.fullWidth)} data-test-id={testId('askContainer')}>
          <LazyAsk product={product} params={params} />
        </div>
      );
    }
    return null;
  }

  makeReviewPhotoGallery = () => {
    const {
      context: {
        marketplace: {
          features: { showReviews }
        }
      },
      props: { reviewGallery },
      handleReviewMediaClick
    } = this;

    if (!showReviews || !shouldRenderReviewGallery(reviewGallery)) {
      return null;
    }

    return (
      <ReviewPhotoGallery
        divClass={cn(css.reviewPhotoGallery, css.fullWidth)}
        includeHr={true}
        reviewGallery={reviewGallery}
        limit={10}
        onOpenMediaReview={handleReviewMediaClick}
        showMediaCount={false}
      />
    );
  };

  onBuyBoxPageContentClick = (e: React.MouseEvent) => {
    const { trackEvent } = this.props;
    const eventTarget = e.target as HTMLElement;
    if (eventTarget.matches('[data-pagecontent-id="pdp-buybox"] a')) {
      e.nativeEvent.stopImmediatePropagation();
      trackEvent('TE_PDP_BUYBOX_CONTENT_CLICK', eventTarget.textContent);
    }
  };

  onSymphonyComponentClick = (e: React.MouseEvent) => {
    // Mostly taken from Landing.jsx container
    const {
      symphonyStory: { productId },
      trackLegacyEvent
    } = this.props;
    const { currentTarget } = e;
    const {
      dataset: { eventlabel, eventvalue, slotindex }
    } = currentTarget as HTMLElement;
    const action = `Detail-${productId}`;
    const label = stripSpecialCharsDashReplace(eventlabel);
    const value = stripSpecialCharsDashReplace(eventvalue);
    const slotIndex = stripSpecialCharsDashReplace(slotindex);

    e.stopPropagation();
    trackLegacyEvent(action, label || null, value);
    trackEvent('TE_PDP_STORIES_CLICK', `${slotIndex}:${label}:${value}`);
  };

  trackPageView = (detail: FormattedProductBundle) => {
    const {
      marketplace: {
        pdp: { showDescriptionSizeChart }
      }
    } = this.context;

    const {
      params: { colorId }
    } = this.props;

    const { defaultProductType, description, styles } = detail;

    const style = ProductUtils.getStyleByColor(styles, colorId);
    const productViewData = ProductUtils.getProductViewData(style, detail);

    // If has the accordion, add its key to the accordion impression event
    const accordions: { accordionState: boolean; headerName: string }[] = [];

    ProductUtils.hasSizeChartAccordionSection({
      defaultProductType,
      description,
      showDescriptionSizeChart
    }) && accordions.push({ accordionState: true, headerName: SIZE_CHART_ACCORDION_HEADING });

    track(() => [pvProduct, { ...productViewData, accordions }]);
  };

  trackLowestRecentPriceView = () => {
    const {
      params: { colorId },
      product
    } = this.props;

    const {
      marketplace: {
        pdp: { hasLowestRecentPrice }
      }
    } = this.context;

    if (!product.detail) {
      return;
    }

    const { productId, styles } = product.detail;
    const style = ProductUtils.getStyleByColor(styles, colorId);
    const styleId = style && style.styleId;
    const isLowestRecentPrice = ProductUtils.isProductLowestRecentPrice(hasLowestRecentPrice, styleId, style, product.lowestPrices);

    if (isLowestRecentPrice) {
      track(() => [evProductLowestRecentPrice, { productId, styleId }]);
    }
  };

  render() {
    const {
      params,
      product,
      reviewGallery,
      productNotify,
      location,
      similarProductRecos,
      metaDescription,
      symphonyStory: { stories, loadingSymphonyStoryComponents },
      isProductTypeShoesOrClothing,
      productCardGenderDisplay,
      isReviewsSummarizationRendered
    } = this.props;

    const {
      marketplace: {
        links,
        search: { hasSponsoredAds },
        recos: { showCustomersWhoViewedThisItemAlsoViewed, showSimilarItemsYouMayLike },
        pdp: { percentOffText, showPercentOffBanner, hasLowestRecentPrice, hasPdpSku },
        features: { showReviews, showMelodyShippingAndReturnsBanner }
      },
      testId
    } = this.context;

    const { sizeChartModalProps } = this.state;

    const { detail, styleThumbnails, selectedSizing, validation, sizingPredictionId, isOnDemandEligible } = product;

    const isLoaded = ProductUtils.isProductDataLoaded(product, params.productId);

    if (!isLoaded) {
      return <PageLoader />;
    }

    if (!detail) {
      return null;
    }

    const {
      defaultProductType,
      productId,
      styles,
      sizing,
      videos,
      reviewSummary,
      reviewCount,
      productRating,
      youtubeVideoId,
      isWearable,
      isReviewableWithMedia,
      youtubeData: { embedUrl, contentUrl, videoName, thumbnailUrl, uploadDate },
      brandName,
      productName,
      defaultImageUrl
    } = detail;

    const showProductSku = hasPdpSku && (!productCardGenderDisplay || !isProductTypeShoesOrClothing);

    const firstInStock = sizing.stockData.find(stock => parseInt(stock.onHand) > 0);

    const style = ProductUtils.getStyleByColor(styles, params.colorId);
    const styleId = style && style.styleId;
    const multiviewImages = ProductUtils.buildAngleThumbnailImages(style, 700, 525);
    const isYouTubeVideo = !!youtubeVideoId;
    const heartsData = this.makeHeartsData();

    const janusPixelQueryParams = {
      item: productId,
      teen: styleId,
      widget: 'RecordViewedItem'
    };
    const { images, badges, finalSale } = style;

    const stock = ProductUtils.getStockBySize(sizing.stockData, style.colorId, selectedSizing);
    const { onHand } = (stock as ProductStockData) || {};

    const isLowestRecentPrice = ProductUtils.isProductLowestRecentPrice(hasLowestRecentPrice, styleId, style, product.lowestPrices);
    const promoBadges = ProductUtils.getPromoBadgesForStyle(style);

    const hasMostHelpfulReviews = shouldRenderMostHelpfulReviews(reviewSummary);
    const hasReviewsToShow = showReviews && parseInt(reviewCount) > 0;
    const link = ProductUtils.getSharingButtonLink(productId, style.colorId);

    const { fbAppId } = links.sharing;
    const linksProductContent = {
      link: link,
      name: `${brandName} ${productName}`,
      style: style.color,
      image: defaultImageUrl
    };

    const firstInvalidDimensionName = ProductUtils.getMissingDimensionName(selectedSizing, sizing);
    const missingDimension = firstInvalidDimensionName && `${firstInvalidDimensionName}_dimension`.toUpperCase();
    const productImages: FeaturedImage[] = getProductImagesFormatted(style?.images, detail?.defaultProductType);
    const isZPromo = ProductUtils.isZPromo(style.taxonomyAttributes);

    const shareIconProps = { fbAppId, linksProductContent, productId, styleId };
    const asin = ProductUtils.getSelectedStyleStockBySize([], sizing, selectedSizing, style.stocks)?.asin || null;

    const onSharingButtonClick = (linkName: string) => {
      ProductUtils.generateShareLinkEvent(linkName, asin, PRODUCT_PAGE);
    };

    // ProductEventData for Amplitude tracking in hearts
    const productEventData = {
      brand: brandName,
      category: defaultProductType,
      gender: ProductUtils.getGender(detail),
      list_price: style.originalPrice,
      name: productName,
      on_sale: style.onSale === 'true', // this is a string, so converts it
      position: 1,
      price: style.price,
      sku: productId,
      styleId: styleId
    };

    return (
      <ProductContextProvider value={product}>
        <SiteMetadata loading={!isLoaded}>
          {/* data-pdp-style-id is to allow marketing to easily access the style-id */}
          <div data-pdp-style-id={styleId} className={css.wrap}>
            <GamSlot slot={PDP_WIDE_TOP} />
            <GamSlot slot={PDP_NARROW_TOP} />
            <ProductBreadcrumbs product={detail} onBack={this.onBack} />
            <div
              ref={el => (this.theater = el)}
              className={css.theater}
              itemScope
              itemType="http://schema.org/Product"
              data-test-id={testId('productDetail')}
            >
              <div className={css.primaryWrapper}>
                <div className={css.productStage} id="productRecap">
                  <div className={css.stickyProductContainer}>
                    <div id="stage" className={css.productMain}>
                      {multiviewImages && multiviewImages.length && (
                        <>
                          {badges?.length > 0 && (
                            <Badge
                              id={badges[0]!.bid}
                              category={badges[0]?.zc}
                              url={badges[0]?.url}
                              classNameWrapper={css.productDetailBadgeRelative}
                              productBadgeWrapperClassName="mt-[24px] sm:mt-[24px]"
                              size="pdp"
                            />
                          )}
                          <ProductGalleryOverlay>
                            <ShareSocialLinks
                              productDetail={shareIconProps}
                              onSharingButtonClick={onSharingButtonClick}
                              wrapperClassName="relative lg:hidden"
                              mobileWrapperClassName="lg:hidden"
                            />
                            <SocialCollectionsWidget
                              canAddNewCollection={true}
                              shouldAddImmediately={true}
                              getStyleId={this.getStyleId}
                              productId={productId}
                              colorId={style.colorId}
                              price={style.price}
                              missingDimension={missingDimension}
                              sourcePage={PRODUCT_PAGE}
                              productImages={productImages}
                              productEventData={productEventData}
                            />
                          </ProductGalleryOverlay>
                          <div className={css.productGalleryWrapper}>
                            <ProductGallery
                              style={style}
                              product={detail}
                              productVideos={videos}
                              isYouTubeVideo={isYouTubeVideo}
                              youtubeSrc={embedUrl}
                              isVideoSelected={this.isVideoSelected}
                              imageHasBadge={badges?.length > 0}
                            />
                          </div>
                          <div className={css.galleryWrapper}>
                            <Gallery images={images} styleId={styleId} product={detail} />
                          </div>
                        </>
                      )}
                    </div>
                    <div className={cn(css.productForm, 'px-5 lg:ml-6 lg:px-0')} data-test-id={testId('buyBoxContainer')}>
                      <div className={cn(css.wing, 'flex flex-col gap-y-6')}>
                        <div className="flex flex-col gap-y-6">
                          <div className="flex flex-col gap-y-1">
                            <ProductName
                              showSocialLinks={false}
                              selectedSizing={selectedSizing}
                              product={detail}
                              colorId={style.colorId}
                              productCardGenderDisplay={productCardGenderDisplay}
                              isProductTypeShoesOrClothing={isProductTypeShoesOrClothing}
                            />
                            {hasReviewsToShow && (
                              <div className={css.reviewSummaryContainer}>
                                <BuyBoxReviewBlurb
                                  isReviewsSummarizationRendered={isReviewsSummarizationRendered}
                                  hasMostHelpfulReviews={hasMostHelpfulReviews}
                                  numReviews={reviewCount}
                                  rating={productRating}
                                />
                              </div>
                            )}
                            {showProductSku && (
                              <div className="md:text-center lg:text-left">
                                <ProductSkuNumber productId={productId} />
                              </div>
                            )}
                          </div>
                          <div className="flex flex-row gap-x-2">
                            <Price
                              productStyle={style}
                              percentOffText={percentOffText}
                              showPercentOffBanner={showPercentOffBanner}
                              defaultProductType={defaultProductType}
                              isAvailable={+onHand > 0}
                              isLowestRecentPrice={isLowestRecentPrice}
                            />
                            {isZPromo && <ZPromo className="mt-2" />}
                          </div>
                          {promoBadges.map((badge, index) => (
                            <PromoBadge key={index} {...badge} disclaimer="* Select styles only" />
                          ))}
                        </div>
                        {styleThumbnails &&
                          this.makeStylePicker({
                            product: detail,
                            style,
                            styleThumbnails,
                            selectedSizing,
                            dimensionValidation: validation,
                            sizingPredictionId,
                            isOnDemandEligible
                          })}
                        <ProductAccordions
                          colorId={params.colorId}
                          product={product}
                          productId={params.productId}
                          openSizeChartModal={this.openSizeChartModal}
                        />
                        <GamSlot slot={PDP_WIDE_MID} className={css.gamSlotContainer} />
                      </div>
                    </div>
                  </div>

                  {/* who viewed also viewed */}
                  <div data-test-id={testId('sideRecoContainer')}>
                    <RecosDetail1
                      styleId={styleId}
                      params={params}
                      onRecoClicked={this.onRecoClicked}
                      similarProductRecos={similarProductRecos}
                      heartsData={heartsData}
                      titleRef={recosDetail1Ref}
                    />
                  </div>

                  <meta itemProp="category" content={defaultProductType} />

                  {/* Complementary Items from Amazon */}
                  {this.props.isAmazonReverseSharingEnabled && <AmazonReverseSharingCarousel heartsData={heartsData} />}

                  {/* discover more items */}
                  {hasSponsoredAds && firstInStock?.id && (
                    <PdpSponsoredAds
                      containerClassName={css.sponsoredAdsContainer}
                      keywords={[defaultProductType]}
                      stockId={firstInStock.id}
                      productId={productId}
                    />
                  )}

                  {/* history */}
                  {!!stories?.length && !loadingSymphonyStoryComponents && (
                    <div className={cn(css.symphonyStoryContainer, css.fullWidth)}>
                      {stories.map((slotData, slotIndex) => (
                        <LandingSlot
                          key={slotData.slotName}
                          slotName={slotData.slotName}
                          slotIndex={slotIndex}
                          data={slotData}
                          onComponentClick={this.onSymphonyComponentClick}
                          shouldLazyLoad={true}
                        />
                      ))}
                    </div>
                  )}

                  {/* recommended for you */}
                  <div className={cn(css.descriptionWrapper, css.fullWidth)}>
                    <GamSlot slot={PDP_NARROW_MID} />
                    <RecosDetail2
                      styleId={styleId}
                      params={params}
                      onRecoClicked={this.onRecoClicked}
                      similarProductRecos={similarProductRecos}
                      heartsData={heartsData}
                    />
                  </div>

                  {this.makeAsk()}
                  <HappyFeatureFeedback
                    additionalFeedbackMessage="Please tell us more about your experience"
                    className={cn(css.feedback, css.fullWidth)}
                    completionMessage="Thank you for your feedback!"
                    feedbackQuestion="Was this page helpful?"
                    feedbackType="PRODUCT_PAGE_EXPERIENCE_FEEDBACK"
                    pageType={PRODUCT_PAGE}
                    source="pdp"
                  />
                  {showReviews && isReviewableWithMedia && isWearable && (
                    <LazyHowItWasWorn pageType={PRODUCT_PAGE} productId={productId} colorId={params.colorId} productName={detail.productName} />
                  )}
                  {!ProductUtils.isShoeType(defaultProductType) && (
                    <hr className="mx-4 mb-6 max-w-layoutMaxWidth border-subtle md:mx-8 lg:mx-auto" />
                  )}
                  {showReviews ? <ReviewPreview params={params} onReviewMediaClick={this.handleReviewMediaClick} /> : null}
                  {showReviews && shouldRenderReviewGallery(reviewGallery) ? (
                    <ReviewGalleryWrapper params={params} returnUrl={buildSeoProductUrl(detail)} />
                  ) : null}
                  {this.makeReviewPhotoGallery()}
                  <div className={css.outOfStock}>{this.makeOutOfStock()}</div>
                </div>
              </div>
              {showCustomersWhoViewedThisItemAlsoViewed && (
                <RecosDetail3
                  styleId={styleId}
                  params={params}
                  onRecoClicked={this.onRecoClicked}
                  similarProductRecos={similarProductRecos}
                  heartsData={heartsData}
                />
              )}
              {isYouTubeVideo && typeof uploadDate === 'string' && uploadDate && (
                <StructuredVideoObject
                  name={videoName}
                  embedUrl={embedUrl}
                  contentUrl={contentUrl}
                  description={metaDescription}
                  thumbnailUrl={thumbnailUrl}
                  uploadDate={uploadDate}
                />
              )}
            </div>
            {showSimilarItemsYouMayLike && (
              <RecosDetail3
                styleId={styleId}
                params={params}
                onRecoClicked={this.onRecoClicked}
                similarProductRecos={similarProductRecos}
                heartsData={heartsData}
              />
            )}
            {showMelodyShippingAndReturnsBanner && !finalSale && <ShippingAndReturnsBanner />}
          </div>
          {productNotify.modalShown && (
            <ProductNotifyModal
              isOpen={productNotify.modalShown}
              onClose={this.onCloseProductNotifyMe}
              product={detail}
              productId={productId}
              colorId={params.colorId}
              selectedSizing={selectedSizing}
              renderRecos={() => this.makeRecosFindYourSize()}
            />
          )}

          <SizeChartModal {...sizeChartModalProps} />
          <JanusPixel location={location} queryParams={janusPixelQueryParams} />
          <UnleashWiringTest />
        </SiteMetadata>
      </ProductContextProvider>
    );
  }
}

function WithHooks(Component: any) {
  const ComponentWithHooks = (props: Props) => {
    const { isEnrolled: isPdpOosUx } = useIsEnrolledInVariant({ featureFlag: FEATURE_PDP_OOS_UX, isZapposOnly: false });
    const { isEnrolled: isAmazonReverseSharingEnabled } = useIsEnrolledInVariant({ featureFlag: FEATURE_PDP_AMAZON_REVERSE_SHARING });
    // we just need the enrollment event to fire, the redux state has the correct URL from the server based on enrollment.
    useIsEnrolledInVariant({ featureFlag: FEATURE_PDP_CLOUDCAT_DIRECT, isZapposOnly: false });
    // fire abTestEnrollment for PDP Kratos experiment (control users stay on Marty, treatment users are redirected server-side)
    useIsEnrolledInVariant({ featureFlag: FEATURE_PDP_KRATOS, isZapposOnly: false });
    return <Component {...props} isAmazonReverseSharingEnabled={isAmazonReverseSharingEnabled} isPdpOosUx={isPdpOosUx} />;
  };
  ComponentWithHooks.fetchDataOnServer = Component.fetchDataOnServer;
  ComponentWithHooks.webpackChunkName = Component.webpackChunkName || Component.displayName || Component.name;
  ComponentWithHooks.displayName = `WithHooks(${Component.displayName || Component.name})`;
  return ComponentWithHooks;
}

export function mapStateToProps(state: AppState, ownProps: any) {
  const {
    cart: { isModalShowing: isCartModalShowing },
    cookies,
    client,
    customer: isRecognizedCustomer,
    reviews: { reviewGallery },
    sharing: { productNotify, linkShare },
    brandPage,
    environmentConfig,
    holmes,
    killswitch: { isShowingThirdPartyAds },
    meta,
    pageLoad: { loaded: pageLoaded },
    pageView,
    product: productState,
    products,
    recos: janusRecos,
    router,
    sharedRewards: { transparencyPointsForItem },
    url,
    ads: { adCustomerId, adEmailHash },
    rewards
  } = state;

  const { inParams, match: { params = {} } = {} } = ownProps || {};

  const recos: RecosState = janusRecos;
  const product: ProductDetailState = productState;
  const { isReviewsSummarizationRendered } = product.reviewData ?? {};
  const metaDescription = meta.documentMeta?.meta?.name?.description || ''; // to use as the SEO description for video as Broadway used to
  const customerFirstName = (holmes && holmes.firstName) || '';
  const directedId = (holmes && holmes.directedId) || '';
  const { imageServer } = environmentConfig;

  const isProductTypeShoesOrClothing = ProductUtils.isProductTypeShoesOrClothing(product?.detail?.defaultProductType);
  const productCardGenderDisplay = ProductUtils.getProductGender(product?.detail?.genders);
  const { brandPromos } = transparencyPointsForItem || {};

  const { enrolled = false } = rewards.rewardsInfo ?? {};

  return {
    params: inParams ?? params,
    isProductTypeShoesOrClothing,
    productCardGenderDisplay,
    brandPage,
    brandPromos,
    isRecognizedCustomer,
    customerFirstName,
    directedId,
    isCartModalShowing,
    isShowingThirdPartyAds,
    metaDescription,
    sessionId: cookies['session-id'] || '',
    similarProductRecos: recos,
    isEnrolledInVip: enrolled ?? false,
    pageLoaded,
    pageView,
    product,
    products,
    productNotify,
    reviewGallery,
    isReviewsSummarizationRendered,
    secureImageBaseUrl: imageServer.url,
    symphonyStory: selectSymphonyStory(state),
    url,
    isGiftCard: ProductUtils.isGiftCard(product?.detail?.defaultProductType),
    linkShare,
    client,
    adCustomerId,
    adEmailHash,
    holmes,
    router
  };
}

export const mapDispatchToProps = {
  addAdToQueue,
  changeQuantity,
  fetchAmazonReverseSharing,
  loadProductDetailPage,
  fetchProductSearchSimilarity,
  fetchProductPageRecos,
  fetchProductReviews,
  fetchProductReviewsWithMedia,
  fetchSizingPrediction,
  getPdpStoriesSymphonyComponents,
  heartProduct,
  hideReviewGalleryModal,
  unHeartProduct,
  pageTypeChange,
  productSizeChanged,
  pushMicrosoftUetEvent,
  submitNotifyBrandEmail,
  showSelectSizeTooltip,
  hideSelectSizeTooltip,
  highlightSelectSizeTooltip,
  unhighlightSelectSizeTooltip,
  validateDimensions,
  setLastSelectedSize,
  setProductDocMeta,
  stockSelectionCompleted,
  toggleHeartingLoginModal,
  toggleOosButton,
  toggleProductNotifyModal,
  showReviewGalleryModal,
  showCartModal,
  productSwatchChange,
  getHearts,
  getProductRelations,
  onLookupRewardsTransparencyPointsForItem,
  productAgeGroupChanged,
  productGenderChanged,
  productSingleShoeSideChanged,
  productSizeRangeChanged,
  productSizeUnitChanged,
  fetchAccountInfo,
  fetchCustomerAuthDetails,
  updateAdData,
  fetchLowestPrices,
  selectedColorChanged,
  firePdpPixelServer
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

const ProductDetailWithHooks = connector(WithHooks(ProductDetail));

export default ProductDetailWithHooks;
